/**
 * Calcule automatiquement le statut d'un événement
 * en fonction de ses dates de début/fin et de la date actuelle.
 * Le statut "annule" est toujours préservé (décision manuelle).
 */
export const computeEventStatus = (
  startDate: string | undefined | null,
  endDate: string | undefined | null,
  currentStatus?: string
): string => {
  // "annulé" est un statut manuel, on le préserve toujours
  if (currentStatus === 'annule') return 'annule'

  // Si pas de dates (ex: magazine), on garde le statut stocké ou "a_venir" par défaut
  if (!startDate || !endDate) {
    return currentStatus || 'a_venir'
  }

  const now = new Date()
  const start = new Date(startDate)
  const end = new Date(endDate)

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return currentStatus || 'a_venir'
  }

  if (now > end) return 'termine'
  if (now >= start && now <= end) return 'en_cours'
  return 'a_venir'
}

/**
 * Calcule le nombre de jours restants avant l'ouverture (statut "a_venir")
 * ou avant la fin de l'exposition (statut "en_cours").
 * Renvoie { type: 'none', days: 0 } pour les expositions terminées, annulées, ou sans dates.
 */
export const getDaysRemainingInfo = (
  startDate: string | undefined | null,
  endDate: string | undefined | null,
  currentStatus?: string
): { type: 'until_start' | 'until_end' | 'none'; days: number } => {
  const status = computeEventStatus(startDate, endDate, currentStatus)
  if (status === 'termine' || status === 'annule') return { type: 'none', days: 0 }
  if (!startDate || !endDate) return { type: 'none', days: 0 }

  const now = new Date()
  const start = new Date(startDate)
  const end = new Date(endDate)
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return { type: 'none', days: 0 }

  const msPerDay = 24 * 60 * 60 * 1000
  if (status === 'a_venir') {
    const days = Math.max(0, Math.ceil((start.getTime() - now.getTime()) / msPerDay))
    return { type: 'until_start', days }
  }
  if (status === 'en_cours') {
    const days = Math.max(0, Math.ceil((end.getTime() - now.getTime()) / msPerDay))
    return { type: 'until_end', days }
  }
  return { type: 'none', days: 0 }
}
